import styled from 'styled-components';
import React, { useState } from 'react';
import {
  Heading,
  ListItem,
  Stack,
  Tag,
  TagText,
  Text,
} from '@alefeducation/ui-elements-library-core';
import {
  CaretDownSolid,
  CaretRightSolid,
} from '@alefeducation/ui-elements-library-icons';
import Details from './Details';
import RNPActions from './RNPActions';
import { useRNPActionsContext } from './RNPActions/context';
import { Role } from '../types';
import { RoleCategory } from '../../../apollo/queries/v3/RolesNPermissions/getAllRolesNPermissions.queries';

type Props = {
  role: Role;
  isViewOnlyRole?: boolean;
  backgroundColor?: string;
};

const StyleListItem = styled.div`
  display: flex;
  width: 100%;
  cursor: pointer;
  align-items: center;
  padding-right: 64px;

  .rnp-role-name {
    width: 30%;
    margin-left: 20px;
  }

  .rnp-permissions {
    width: 30%;
  }

  .rnp-module {
    width: 30%;
  }

  .rnp-actions {
    width: 10%;
  }
`;

const StyledItemContainer = styled(ListItem)<{ backgroundColor?: string }>`
  display: flex;
  flex-direction: column;

  padding: 22px 0px 20px 24px;
  background-color: ${({ backgroundColor }) => backgroundColor};

  .rnp-actions {
    visibility: hidden;
  }

  :hover {
    background-color: ${({ backgroundColor }) => backgroundColor};

    .rnp-actions {
      visibility: visible;
    }
  }
`;

const StyledTagList = styled(Stack)`
  > div:first-child {
    display: flex;
    flex-wrap: wrap;
    row-gap: 8px;
    column-gap: 8px;
  }
`;

const StyledToggleIconContainer = styled.span`
  height: 16px;
`;

const RoleItem = ({ role, backgroundColor, isViewOnlyRole }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { name, descriptionText, permissionsText, permissions, modules } = role;
  const { onEdit } = useRNPActionsContext();

  const toggleDetails = (e: any) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const renderTags = () => {
    return modules.map((module: RoleCategory, i: number) => {
      return (
        <Tag padding="thick" key={i}>
          <TagText>{module.name}</TagText>
        </Tag>
      );
    });
  };

  return (
    <StyledItemContainer backgroundColor={backgroundColor}>
      <StyleListItem onClick={() => onEdit(role.id)}>
        <StyledToggleIconContainer onClick={toggleDetails}>
          {isOpen ? (
            <CaretDownSolid
              height="12"
              width="12"
              color="primary.100"
              data-testid="rnp-caret-down"
            />
          ) : (
            <CaretRightSolid
              height="12"
              width="12"
              color="primary.100"
              data-testid="rnp-caret-right"
            />
          )}
        </StyledToggleIconContainer>
        <Stack
          flexDirection="column"
          justifyContent="center"
          className="rnp-role-name"
        >
          <Heading variant="heading-small" data-testid={'rnp-row-field-name'}>
            {name}
          </Heading>
        </Stack>
        <Stack
          flexDirection="column"
          justifyContent="center"
          className="rnp-permissions"
        >
          <Text
            data-testid={'rnp-row-field-email'}
            variant="body-large"
            color="primary.100"
            weight="light"
          >
            {permissions}
          </Text>
        </Stack>
        <StyledTagList
          flexDirection="column"
          justifyContent="center"
          key="rnp-module"
          className="rnp-module"
        >
          {renderTags()}
        </StyledTagList>
        {!isViewOnlyRole && (
          <Stack className="rnp-actions">
            <RNPActions id={role.id} name={role.name} />
          </Stack>
        )}
      </StyleListItem>

      {isOpen && (
        <Details
          descriptionText={descriptionText}
          permissionsText={permissionsText}
        />
      )}
    </StyledItemContainer>
  );
};

export default RoleItem;
